import type { AxiosRequestConfig } from "axios"

import axios from "axios"
import dayjs from "dayjs"
import { toast } from "sonner"
import { paths } from "@/src/routes/paths"
import { showBadInternetToast, dismissBadInternetToast } from "@/src/components/badInternetToast/badInternetToast"

import { CONFIG } from "src/config-global"

// ----------------------------------------------------------------------
const REQUEST_RETRY_HEADER = "Pph-Token-Retry"
const REQUEST_API_TIMEOUT_RETRY_HEADER = "Pph-Token-Api-Timeout-Retry"

const axiosInstance = axios.create({ baseURL: CONFIG.site.serverUrl })

// Convert all top-level dates to UTC
axiosInstance.interceptors.request.use((req) => {
    if (req.method === "post" || req.method === "put") {
        try {
            const dataJson = req.data
            Object.entries(dataJson).forEach(([key, value]) => {
                if (key.toLowerCase().includes("date")) {
                    if (typeof value === "string" && dayjs(value).isValid()) {
                        dataJson[key] = dayjs(value).toISOString()
                    }
                }
            })
            req.data = dataJson
        } catch (e) {
            return req
        }
    }
    return req
})

axiosInstance.interceptors.response.use(
    (response) => {
        dismissBadInternetToast()
        return response
    },
    (error) => {
        const statusCode = error.response?.status
        const originalRequestConfig = error.config

        if (statusCode === 401 && !originalRequestConfig.headers[REQUEST_RETRY_HEADER]) {
            const { url } = error.config
            if (url !== endpoints.auth.signIn && url !== endpoints.auth.signUp && url !== endpoints.auth.getNewToken) {
                return getTokenFromRefresh().then((newToken) => {
                    if (newToken) {
                        originalRequestConfig.headers.Authorization = `Bearer ${newToken}`
                        originalRequestConfig.headers[REQUEST_RETRY_HEADER] = "true"
                        return axiosInstance.request(originalRequestConfig)
                    }
                    toast.error("Please sign in again")
                    setTimeout(() => {
                        window.location.href = paths.auth.jwt.signIn
                    }, 1000)
                    dismissBadInternetToast()
                    return Promise.reject((error.response && error.response.data) || "Something went wrong!")
                })
            }
        }

        if (
            error.code !== "ERR_CANCELED" &&
            error.code !== "ECONNABORTED" &&
            error.code !== "ERR_NETWORK" &&
            error.config.url !== endpoints.personalWhatsapp.disconnect &&
            error.config.url !== endpoints.databaseSavedSearches.list &&
            error.config.url !== endpoints.leads.publicLeadAiStatus &&
            error.config.url !== endpoints.file.parseResume &&
            !error.config.url.startsWith(endpoints.otp.verify)
        ) {
            dismissBadInternetToast()
            toast.error(
                error.response?.data
                    ? (error.response.data.errorMessage ??
                          error.response.data.message ??
                          error.response.data.statusMessage ??
                          error.response.data)
                    : "Something went wrong"
            )
        } else if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
            if (!originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER]) {
                originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER] = "true"
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(axiosInstance.request(originalRequestConfig))
                    }, 2000)
                })
            }
            showBadInternetToast()
        }
        dismissBadInternetToast()
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject(
            error.response
                ? { ...error.response.data, httpError: error }
                : { message: "Something went wrong", httpError: error }
        )
    }
)

export default axiosInstance

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
    try {
        const [url, config] = Array.isArray(args) ? args : [args]

        const res = await axiosInstance.get(url, { ...config })

        return res.data
    } catch (error) {
        console.error("Failed to fetch:", error)
        throw error
    }
}

export function removeUserToken() {
    localStorage.removeItem("jwt_access_token")
    localStorage.removeItem("jwt_refresh_token")
    delete axios.defaults.headers.common.Authorization
    delete axiosInstance.defaults.headers.common.Authorization
}

export async function getTokenFromRefresh(): Promise<string | undefined> {
    const refreshToken = localStorage.getItem("jwt_refresh_token")
    if (!refreshToken) {
        removeUserToken()
        return undefined
    }

    try {
        const resp = await axiosInstance.post(
            endpoints.auth.getNewToken,
            { refreshToken },
            {
                transformRequest: (data, headers) => {
                    headers["Content-Type"] = "application/json"
                    delete headers.Authorization
                    return JSON.stringify(data)
                },
            }
        )
        const accessToken = resp?.data?.data?.token
        const newRefreshToken = resp?.data?.data?.refreshToken

        if (accessToken) {
            localStorage.setItem("jwt_access_token", accessToken)
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`
            if (newRefreshToken) localStorage.setItem("jwt_refresh_token", newRefreshToken)
            return accessToken
        }
        removeUserToken()
        return undefined
    } catch (error) {
        removeUserToken()
        return undefined
    }
}

export const isNetworkError = (error: any) => {
    if (error?.httpError) return error?.httpError?.code === "ECONNABORTED" || error?.httpError?.code === "ERR_NETWORK"
    return (
        error?.errorMessage?.clientMessage === "Network Error" ||
        error?.errorMessage?.clientMessage?.includes("timeout")
    )
}

// ----------------------------------------------------------------------

export const endpoints = {
    chat: "/api/chat",
    config: {
        appConfig: "/pph/api/config/status-config",
    },
    personalWhatsapp: {
        whatsappConnectionStatus: "/pph/api/personal-whatsapp/status",
        whitelist: "/pph/api/personal-whatsapp/whitelist",
        connect: "/pph/api/personal-whatsapp/connect",
        disconnect: "/pph/api/personal-whatsapp/disconnect",
        getTemplate: "/pph/api/personal-whatsapp/template",
        sendMessage: "/pph/api/personal-whatsapp/send-message",
    },
    auth: {
        me: "pph/api/users/profile",
        googleLogin: "/pph/api/auth/google/login",
        msLogin: "/pph/api/auth/microsoft/login",
        signIn: "/pph/api/auth/login",
        signUp: "/pph/api/auth/signup",
        users: "/pph/api/users/all",
        getNewToken: "/pph/api/auth/refresh-token",
    },
    selections: {
        list: "/pph/api/selections/all",
        details: "http://localhost:3000/selection",
        statusUpdate: "/pph/api/selections",
        selectionForm: "/pph/api/selections",
    },
    interviews: {
        list: "/pph/api/interviews/all",
        details: "/pph/api/interviews/",
        post: "/pph/api/interviews",
        postBulk: "/pph/api/interviews/bulk",
        update: "/pph/api/interviews",
        roundUpdate: "/pph/api/interviews",
    },
    mandates: {
        list: "/pph/api/mandates/all",
        details: "/pph/api/mandates/",
        jobDetails: "/pph/api/mandates/apna-job-details",
        bulkSource: "/pph/api/bulk-jobs",
        detailsPublic: "/pph/api/public/mandate/",
        update: "/pph/api/mandates",
        draft: "/pph/api/mandates/draft",
        dropdown: "/pph/api/mandates/dropdown",
        activate: "/pph/api/mandates/:id/activate",
        templates: "/pph/api/config/screening-questions-template",
        aiRecruiterType: "/pph/api/mandates/agents",
        aiRecruiterTypeForMandate: (mandateId: string) => `/pph/api/mandates/${mandateId}/recommended-agents`,
        enableAi: (mandateId: string) => `/pph/api/mandates/${mandateId}/agent`,
    },
    clients: {
        list: "/pph/api/clients/all",
        details: "/pph/api/clients/",
        clientForm: "/pph/api/clients",
    },
    leads: {
        list: "/pph/api/leads/all",
        statusUpdate: "/pph/api/leads",
        update: "/pph/api/leads",
        details: "/pph/api/leads/",
        leadsForm: "/pph/api/leads",
        creatLeads: "/pph/api/public-leads/job-link",
        publicCreateLeadsV2: "/pph/api/public-leads/walk-in",
        publicLeadAiStatus: "/pph/api/public-leads/get-ai-status",
        callback: "/pph/api/leads/schedule-callback",
        exportLeads: "/pph/api/bulk-jobs",
        reEvaluate: "/pph/api/lead-scoring/re-evaluate-lead",
        requestPublicInterviewPhoneCall: (leadId: string) => `/pph/api/public-leads/${leadId}/trigger-outbound-call`,
        generateOtpForCandidate: "/pph/api/mandates/generate-otp",
    },
    file: {
        upload: "/pph/api/leads/parse/resume",
        genericUpload: "/pph/api/leads/upload/file?fileType=",
        parseResume: "/pph/api/public/parse/resume",
    },
    users: {
        getRecruiters: "/pph/api/leads/all",
        list: "/pph/api/users/company-users",
        addUser: "/pph/api/users/create",
        editUser: "/pph/api/users",
        updateUser: "/pph/api/users/update",
        managerList: "/pph/api/users/company/managers",
        rolePermission: "/pph/api/admin/permissions",
        roleAssign: "/pph/api/admin/assign-permission",
        deletePermission: "/pph/api/admin/remove-permission",
        allRoles: "/pph/api/admin/roles",
    },
    bulkUpload: {
        list: "/pph/api/bulk-jobs/all",
        uploadLeads: "/pph/api/bulk-jobs",
        csvUpload: "/pph/api/config/signed-url/file-upload",
        resumeUploadPublic: "/pph/api/public/signed-url/file-upload",
        fileDownload: "/pph/api/config/signed-url/file-download-from-url",
    },
    jobTitle: {
        list: "/pph/api/suggester?size=20",
    },
    ai: {
        generateCandidateEntityDetails: "/pph/api/mandates/extract-mandate-attributes",
        extractMandateDetailsFromPrompt: "/pph/api/mandates/extract-mandate-details",
    },
    onboarding: {
        fetchSteps: "/pph/api/users/onboarding-steps-completion",
        onboardingCompleted: "/pph/api/users/onboarding-completed",
    },
    credits: {
        list: "/pph/api/coins/all",
    },
    otp: {
        verify: "/pph/api/auth/verify-otp",
        send: "/pph/api/auth/send-otp",
    },
    databaseSavedSearches: {
        list: "/pph/api/bulk-jobs/magic-jobs/get-saved-searches",
        count: "/pph/api/bulk-jobs/magic-jobs/get-saved-search-count",
    },
    company: {
        updateLogo: "/pph/api/companies/update-logo",
    },
}
